import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { CheckCircle2, FileText, Loader2, UserPlus } from "lucide-react";
import { money } from "@/lib/format";
import { useCart } from "@/lib/cart";
import { trackPurchase } from "@/lib/analytics";

export const Route = createFileRoute("/gracias/$token")({
  head: () => ({ meta: [{ title: "Gracias — Truck Company Center" }, { name: "robots", content: "noindex" }] }),
  component: Gracias,
});

function Gracias() {
  const { token } = Route.useParams();
  const { clear } = useCart();
  const [order, setOrder] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setHasSession(!!data.session));
  }, []);

  const sessionId = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("session_id")
    : null;

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 15; // 15 * 2s = 30s

    async function fetchOrder() {
      const { data, error } = await (supabase as any).rpc("get_order_by_token", { p_token: token });
      if (cancelled) return null;
      if (error) { setErr(error.message); return null; }
      const row = Array.isArray(data) ? data[0] : data;
      setOrder(row);
      return row;
    }

    async function verifyWithStripe() {
      if (!sessionId) return false;
      try {
        const res = await fetch("/api/public/verify-checkout-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, intake_token: token }),
        });
        if (!res.ok) return false;
        const j = await res.json();
        return !!j?.paid;
      } catch {
        return false;
      }
    }

    (async () => {
      const row = await fetchOrder();
      if (sessionId && row && row.status === "nuevo") {
        setVerifying(true);
        setExhausted(false);
        // Try verification immediately so we don't wait 2s for the first poll.
        const paidNow = await verifyWithStripe();
        if (cancelled) return;
        if (paidNow) {
          await fetchOrder();
          setVerifying(false);
          clear();
          return;
        }
        const poll = async () => {
          attempts += 1;
          await verifyWithStripe();
          const r = await fetchOrder();
          if (cancelled) return;
          if (r && r.status !== "nuevo") {
            setVerifying(false);
            clear();
            return;
          }
          if (attempts >= maxAttempts) {
            setVerifying(false);
            setExhausted(true);
            return;
          }
          setTimeout(poll, 2000);
        };
        setTimeout(poll, 2000);
      } else if (sessionId && row && row.status !== "nuevo") {
        clear();
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, retryNonce]);

  const paid = !!(order?.status && order.status !== "nuevo");

  // Fire purchase exactly once per token, only when paid.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!paid || !order) return;
    const key = `purchase_tracked_${token}`;
    try {
      if (window.sessionStorage.getItem(key)) return;
      trackPurchase({
        id: order.id,
        order_short: order.order_short,
        total: Number(order.total) || 0,
        items: Array.isArray(order.items) ? order.items : [],
      });
      window.sessionStorage.setItem(key, "1");
    } catch {
      /* ignore */
    }
  }, [paid, order, token]);

  if (err) return <div className="mx-auto max-w-2xl px-4 py-20 text-destructive">Error: {err}</div>;
  if (!order) return <div className="mx-auto max-w-2xl px-4 py-20 text-muted-foreground">Cargando...</div>;

  return (
    <div className="mx-auto max-w-2xl px-4 py-16 md:px-6 md:py-24">
      <div className="rounded-xl border border-hairline bg-surface p-8 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
        <h1 className="mt-4 font-display text-3xl font-semibold text-foreground">
          {sessionId ? (paid ? "¡Pago confirmado!" : "Procesando tu pago...") : "¡Pedido recibido!"}
        </h1>
        <p className="mt-2 text-muted-foreground">
          {sessionId && verifying ? (
            <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Confirmando con Stripe...</span>
          ) : sessionId && !paid && exhausted ? (
            "No pudimos confirmar el pago todavía. Si ya completaste el pago, espera unos minutos o contáctanos."
          ) : sessionId && !paid ? (
            "Tu pago puede tardar unos minutos en confirmarse. Recibirás un email cuando se confirme. Puedes refrescar esta página."
          ) : (
            "Tu siguiente paso es completar el formulario de datos del pedido. Con esos datos arrancamos el proceso de tu compañía."
          )}
        </p>
        {sessionId && !paid && exhausted && (
          <Button
            size="sm"
            variant="outline"
            className="mt-4 border-hairline"
            onClick={() => { setExhausted(false); setRetryNonce((n) => n + 1); }}
          >
            Verificar pago nuevamente
          </Button>
        )}
        <div className="mt-6 rounded-md border border-hairline bg-surface-2 p-4 text-left text-sm">
          {order.items?.map((it: any, i: number) => (
            <div key={i} className="flex justify-between text-foreground">
              <span>{it.name}</span><span>{money(it.price)}</span>
            </div>
          ))}
          <div className="mt-3 flex justify-between border-t border-hairline pt-3 font-semibold text-foreground">
            <span>Total</span><span>{money(order.total)}</span>
          </div>
        </div>
        {paid ? (
          <Link to="/intake/$token" params={{ token }}>
            <Button size="lg" className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90">
              <FileText className="mr-2 h-4 w-4" /> Llenar formulario de datos del pedido
            </Button>
          </Link>
        ) : (
          <Button size="lg" disabled className="mt-6 bg-primary/40 text-primary-foreground cursor-not-allowed">
            <FileText className="mr-2 h-4 w-4" /> Llenar formulario de datos del pedido
          </Button>
        )}
        <p className="mt-3 text-[11px] text-muted-foreground">Guarda este enlace: también te lo enviamos por email.</p>
      </div>

      {paid && !hasSession && (
        <div className="mt-6 rounded-xl border border-primary/40 bg-primary/10 p-6 text-left">
          <div className="flex items-start gap-3">
            <UserPlus className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div className="flex-1">
              <div className="font-medium text-foreground">Crea tu cuenta para seguir tu pedido</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Con una cuenta puedes ver el estado de tu pedido, tus formularios y todo tu historial en cualquier momento.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to="/cuenta/registro">
                  <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">Crear cuenta</Button>
                </Link>
                <Link to="/cuenta/login">
                  <Button size="sm" variant="outline" className="border-hairline">Ya tengo cuenta</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasSession && (
        <div className="mt-6 text-center">
          <Link to="/cuenta" className="text-sm text-primary hover:underline">Ver este pedido en mi cuenta →</Link>
        </div>
      )}
    </div>
  );
}
